import { isValidElement, type ReactNode } from "react";
import { Popover, Space, Typography } from "antd";
import "./identity-user-text.css";

export type IdentityUserDetailItem = {
  key?: string;
  label?: ReactNode;
  value: ReactNode;
};

export type IdentityUserTextProps = {
  userId?: string | number | null;
  username?: string | null;
  fullName?: string | null;
  displayText?: ReactNode;
  fallback?: string;
  detailItems?: IdentityUserDetailItem[];
  detailLabels?: {
    userId?: ReactNode;
    username?: ReactNode;
    fullName?: ReactNode;
  };
  showDefaultDetails?: boolean;
};

function normalizeText(value?: string | number | null) {
  return value == null ? "" : String(value).trim();
}

export function IdentityUserText({
  userId,
  username,
  fullName,
  displayText,
  fallback = "-",
  detailItems,
  detailLabels,
  showDefaultDetails = Boolean(detailLabels),
}: IdentityUserTextProps) {
  const normalizedUserId = normalizeText(userId);
  const normalizedUsername = normalizeText(username);
  const normalizedFullName = normalizeText(fullName);
  const normalizedFallback = normalizeText(fallback) || "-";
  const display = normalizedUsername || normalizedFullName || normalizedUserId || normalizedFallback;
  const content = displayText == null
    ? <Typography.Text>{display}</Typography.Text>
    : isValidElement(displayText)
      ? displayText
      : <Typography.Text>{displayText}</Typography.Text>;
  const defaultDetailItems: IdentityUserDetailItem[] = [
    ...(normalizedUserId
      ? [{
          key: "user-id",
          label: detailLabels?.userId,
          value: normalizedUserId,
        }]
      : []),
    ...(normalizedUsername
      ? [{
          key: "username",
          label: detailLabels?.username,
          value: normalizedUsername,
        }]
      : []),
    ...(normalizedFullName
      ? [{
          key: "full-name",
          label: detailLabels?.fullName,
          value: normalizedFullName,
        }]
      : []),
  ];
  const resolvedDetailItems = detailItems && detailItems.length > 0
    ? detailItems
    : showDefaultDetails
      ? defaultDetailItems
      : normalizedFullName
        ? [{ key: "full-name", value: normalizedFullName }]
        : [];

  if (resolvedDetailItems.length > 0) {
    return (
      <Popover
        trigger="hover"
        content={(
          <Space orientation="vertical" size={8}>
            {resolvedDetailItems.map((item, index) => (
              <div className="airange-identity-user-detail-row" key={item.key ?? `${index}`}>
                <div className="airange-identity-user-detail-main">
                  {item.label ? <Typography.Text type="secondary">{item.label}</Typography.Text> : null}
                  <div>
                    <Typography.Text>{item.value}</Typography.Text>
                  </div>
                </div>
              </div>
            ))}
          </Space>
        )}
      >
        {content}
      </Popover>
    );
  }

  return content;
}
